import { useGame } from '@dovefiniscono/core/game/GameProvider';
import {
  type Game,
  POINTS,
  type Source,
  TIER_NAMES,
  TIER_POINTS,
} from '@dovefiniscono/core/game/game';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import type { Tone } from '../../lib/sections';
import { Icon } from '../../ui/Icon';
import styles from './Game.module.css';
import { MINE_TONE, SOURCE_LABEL, SOURCE_TONE } from './parts';

type Stamp = { id: number; tone: Tone; sign: ReactNode; title: string; text: string };

/** How long a stamp stays, and how many at once. */
const STAY_MS = 5000;
const MAX_STAMPS = 4;
/** Right after opening, the data arrives from the device and the server: that is not news. */
const SETTLE_MS = 4000;

/** What changed between two moments of the game, as stamps to show. */
export function stampsBetween(before: Game, after: Game): Omit<Stamp, 'id'>[] {
  const stamps: Omit<Stamp, 'id'>[] = [];
  if (after.level > before.level) {
    stamps.push({
      tone: 'oggi',
      sign: after.level,
      title: `Livello ${after.level}`,
      text: after.rank !== before.rank ? `Nuovo titolo: ${after.rank}.` : after.rank,
    });
  }
  for (const badge of after.badges) {
    const old = before.badges.find((item) => item.id === badge.id);
    if (!old || badge.tier <= old.tier) continue;
    stamps.push({
      tone: SOURCE_TONE[badge.section],
      sign: TIER_NAMES[badge.tier - 1],
      title: `Distintivo ${badge.title}, grado ${TIER_NAMES[badge.tier - 1]}`,
      // "Giorni con le tre sfide: 1", so the count never fights the plural.
      text: `${badge.unit.charAt(0).toUpperCase()}${badge.unit.slice(1)}: ${badge.value.toLocaleString('it-IT')} · +${TIER_POINTS[badge.tier - 1]} punti`,
    });
  }
  const sameDay = before.quests.every((quest, index) => after.quests[index]?.id === quest.id);
  if (sameDay) {
    for (const quest of [...after.quests, after.weekly, ...after.myDaily, ...after.myWeekly]) {
      const old = [...before.quests, before.weekly, ...before.myDaily, ...before.myWeekly].find(
        (item) => item.id === quest.id,
      );
      if (!quest.met || !old || old.met) continue;
      stamps.push({
        tone: quest.mine ? MINE_TONE[quest.mine.metric] : SOURCE_TONE[quest.section],
        sign: <Icon name="check" />,
        title: quest.mine
          ? 'La tua sfida è fatta'
          : quest.section === 'settimana'
            ? 'Sfida della settimana vinta'
            : 'Sfida completata',
        text: `${quest.title} · +${quest.points} punti`,
      });
    }
    if (after.perfectToday && !before.perfectToday) {
      stamps.push({
        tone: 'oggi',
        sign: '3/3',
        title: 'Giornata perfetta',
        text: `Tutte e tre le sfide di oggi · +${POINTS.perfectDay} punti`,
      });
    }
  }
  if (after.reviews > before.reviews) {
    stamps.push({
      tone: 'oggi',
      sign: <Icon name="check" />,
      title: 'Revisione della settimana fatta',
      text: `+${POINTS.weeklyReview} punti`,
    });
  }
  for (const source of ['focus', 'attivita', 'calendario'] as Source[]) {
    const gained = after.bySource[source] - before.bySource[source];
    if (gained <= 0) continue;
    stamps.push({
      tone: SOURCE_TONE[source],
      sign: `+${gained}`,
      title: `+${gained} punti`,
      text: SOURCE_LABEL[source],
    });
  }
  return stamps;
}

/** Stamps in the corner for what was just earned, wherever in the app it happened. */
export function GameToasts() {
  const game = useGame();
  const previous = useRef<Game | null>(null);
  const openedAt = useRef(Date.now());
  const nextId = useRef(0);
  const [stamps, setStamps] = useState<Stamp[]>([]);

  useEffect(() => {
    const before = previous.current;
    previous.current = game;
    if (!before || Date.now() - openedAt.current < SETTLE_MS) return;
    const fresh = stampsBetween(before, game).map((stamp) => ({ ...stamp, id: nextId.current++ }));
    if (fresh.length === 0) return;
    setStamps((current) => [...current, ...fresh].slice(-MAX_STAMPS));
    const ids = new Set(fresh.map((stamp) => stamp.id));
    setTimeout(
      () => setStamps((current) => current.filter((stamp) => !ids.has(stamp.id))),
      STAY_MS,
    );
  }, [game]);

  return (
    <div className={styles.toasts} aria-live="polite">
      {stamps.map((stamp) => (
        <div key={stamp.id} className={styles.toast} data-tone={stamp.tone}>
          <span className={styles.toastSign} aria-hidden="true">
            {stamp.sign}
          </span>
          <span className={styles.toastBody}>
            <span className={styles.toastTitle}>{stamp.title}</span>
            <span className={styles.toastText}>{stamp.text}</span>
          </span>
          <button
            type="button"
            className={styles.toastClose}
            aria-label="Chiudi"
            onClick={() => setStamps((current) => current.filter((item) => item.id !== stamp.id))}
          >
            <Icon name="close" />
          </button>
        </div>
      ))}
    </div>
  );
}
